import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from "./AuthContext";
import PrivateRoute from "./PrivateRoute";
import Home from "./home";
import "./App.css";
import { BasketProvider } from "./basketContext";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import PendingAuthRedirect from "./components/PendingAuthRedirect";
import Seo from "./components/Seo";
import WebMcpTools from "./components/WebMcpTools";
import ScrollToTop from "./components/ScrollToTop";

// The homepage should not parse checkout, account, generation and admin code.
// Each route is fetched only when a visitor navigates to it.
const Login = lazy(() => import("./login"));
const GeneratedPrint = lazy(() => import("./GeneratedPrint"));
const OrderConfirmation = lazy(() =>
  import("./pages/orderConfirmation/OrderConfirmation")
);
const Account = lazy(() => import("./pages/Account/Account"));
const Prints = lazy(() => import("./pages/Prints/Prints"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ReturnsPolicy = lazy(() => import("./pages/ReturnsPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Collections = lazy(() => import("./pages/Collections/Collections"));
const GeneratePrintV2 = lazy(() =>
  import("./pages/GeneratePrintV2/GeneratePrintV2")
);
const GeneratePrintChat = lazy(() =>
  import("./pages/GeneratePrintChat/GeneratePrintChat")
);
const AdminLayout = lazy(() => import("./pages/Admin/AdminLayout"));
const StatsDashboard = lazy(() => import("./pages/Admin/StatsDashboard"));
const ImageManagement = lazy(() => import("./pages/Admin/ImageManagement"));
const PartnerApplications = lazy(() =>
  import("./pages/Admin/PartnerApplications")
);
const ArticleManagement = lazy(() => import("./pages/Admin/ArticleManagement"));
const ArticleEditor = lazy(() => import("./pages/Admin/ArticleEditor"));
const PinterestStudio = lazy(() => import("./pages/Admin/PinterestStudio"));
const LinkManagement = lazy(() => import("./pages/Admin/LinkManagement"));
const PinterestIdea = lazy(() => import("./pages/Ideas/PinterestIdea"));
const McpAuthorize = lazy(() => import("./pages/MCP/McpAuthorize"));
const McpConnect = lazy(() => import("./pages/MCP/McpConnect"));
const Checkout = lazy(() => import("./pages/Checkout/Checkout"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Developers = lazy(() => import("./pages/Developers"));
const Articles = lazy(() => import("./pages/Articles/Articles"));
const Article = lazy(() => import("./pages/Articles/Article"));

// Routes that hand off from another app and should show nothing but the task
// at hand — no nav, no footer, nowhere to wander off to.
const CHROMELESS_ROUTES = ["/mcp/authorize"];

// App-like routes that fill the viewport. The footer would sit under the
// message composer and give the page a second scroll region.
const FOOTERLESS_ROUTES = ["/generate-print"];
const FOOTERLESS_PREFIXES = ["/admin"];

function SiteChrome({ children, part }) {
  const { pathname } = useLocation();
  if (CHROMELESS_ROUTES.includes(pathname)) return null;
  if (
    part === "footer" &&
    (FOOTERLESS_ROUTES.includes(pathname) ||
      FOOTERLESS_PREFIXES.some((prefix) => pathname.startsWith(prefix)))
  ) {
    return null;
  }
  return children;
}

function RouteBoundary({ children }) {
  const { pathname } = useLocation();
  if (pathname === "/") return children;
  return (
    <Suspense
      fallback={
        // Prerendered inner pages are replaced by a clean client mount. Keep
        // the footer below the viewport while the route chunk resolves;
        // otherwise it briefly occupies the whole screen before the page is
        // inserted and creates a near-1.0 layout shift.
        <div className="min-h-screen" aria-hidden="true" />
      }
    >
      {children}
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="flex flex-col min-h-screen">
        <Router>
          <ScrollToTop />
          <AuthProvider>
            <BasketProvider>
              <Seo />
              <WebMcpTools />
              <PendingAuthRedirect />
              <SiteChrome>
                <Header />
              </SiteChrome>
              <RouteBoundary>
              <Routes>
                <Route exact path="/login" Component={Login} />
                <Route exact path="/" element={<Home />} />
                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <PrivateRoute>
                      <AdminLayout />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<StatsDashboard />} />
                  <Route path="images" element={<ImageManagement />} />
                  <Route path="partners" element={<PartnerApplications />} />
                  <Route path="articles" element={<ArticleManagement />} />
                  <Route path="articles/new" element={<ArticleEditor />} />
                  <Route path="articles/:id" element={<ArticleEditor />} />
                  <Route path="pinterest" element={<PinterestStudio />} />
                  <Route path="links" element={<LinkManagement />} />
                </Route>

                <Route
                  exact
                  path="/generate-print"
                  element={
                    <PrivateRoute>
                      <GeneratePrintChat />
                    </PrivateRoute>
                  }
                />
                {/* The pre-chat form, kept for anyone who prefers the
                    step-by-step controls. */}
                <Route
                  exact
                  path="/generate-print/classic"
                  element={
                    <PrivateRoute>
                      <GeneratePrintV2 />
                    </PrivateRoute>
                  }
                />
                <Route
                  exact
                  path="/print/:slug/:id"
                  element={<GeneratedPrint />}
                />
                <Route exact path="/ideas/:slug" element={<PinterestIdea />} />
                <Route
                  exact
                  path="/order-confirmation"
                  element={<OrderConfirmation />}
                />
                <Route
                  exact
                  path="/checkout/complete"
                  element={<OrderConfirmation />}
                />
                <Route
                  exact
                  path="/account"
                  element={
                    <PrivateRoute>
                      <Account />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/account/:section"
                  element={
                    <PrivateRoute>
                      <Account />
                    </PrivateRoute>
                  }
                />
                <Route exact path="/prints" element={<Prints />} />
                {/* Crawlable pagination. Prerendered per page, so the whole
                    catalogue is reachable by following links rather than only
                    from the sitemap. */}
                <Route exact path="/prints/page/:page" element={<Prints />} />
                <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route exact path="/returns-policy" element={<ReturnsPolicy />} />
                <Route exact path="/shipping-policy" element={<ShippingPolicy />} />
                <Route exact path="/terms" element={<TermsOfUse />} />
                <Route exact path="/contact" element={<Contact />} />
                <Route exact path="/about" element={<About />} />
                <Route exact path="/developers" element={<Developers />} />
                <Route exact path="/articles" element={<Articles />} />
                <Route exact path="/articles/:slug" element={<Article />} />
                <Route exact path="/collections" element={<Collections />} />
                <Route exact path="/collections/:key" element={<Collections />} />
                <Route path="/collections/:key/:value" element={<Collections />} />
                {/* A collection with more prints than fit on one page needs
                    real pages behind the links its pager renders, or every
                    print past the first 24 is unreachable. */}
                <Route
                  path="/collections/:key/:value/page/:page"
                  element={<Collections />}
                />
                <Route path="/mcp/authorize" element={<McpAuthorize />} />
                <Route path="/mcp/connect" element={<McpConnect />} />
                <Route path="/checkout" element={<Checkout />} />
              </Routes>
              </RouteBoundary>
            </BasketProvider>
          </AuthProvider>
          <SiteChrome part="footer">
            <Footer />
          </SiteChrome>
        </Router>
      </div>
    </HelmetProvider>
  );
}

export default App;
